/**
 * Minimal, XSS-safe markdown → HTML for the research reports. The reports only
 * ever emit a fixed subset (headings, GitHub tables, bullet lists, blockquotes,
 * `code`, **bold**, *em*, `---` rules), so this targets exactly that — no
 * third-party markdown/sanitizer dependency.
 *
 * Safety: every source line is HTML-escaped FIRST, then a small set of inline
 * patterns is applied to the already-escaped text, so no report content can ever
 * inject markup. Table cells are additionally tagged with a class by their
 * verbatim value (pass / fail / warn / number) for colouring.
 */

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inline(text: string): string {
  let out = esc(text).replace(/\\\|/g, "|").replace(/\\_/g, "_");
  out = out.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/`([^`]+?)`/g, "<code>$1</code>");
  out = out.replace(/(^|[^\w/])\*(?!\s)(.+?)(?<!\s)\*/g, "$1<em>$2</em>");
  return out;
}

function cellClass(raw: string): string {
  const t = raw.trim().replace(/\*/g, "").toLowerCase();
  if (t === "yes") return "ok";
  if (t === "no") return "muted";
  if (raw.includes("⚠")) return "warn";
  if (/^[+\-]?\d/.test(t) || t.endsWith("pp") || t.endsWith("%")) return "num";
  return "";
}

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
}

const SEP_RE = /^\s*\|[\s:|-]+\|\s*$/;

/** Render the report markdown subset to an HTML string. */
export function renderMarkdown(md: string): string {
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let i = 0;
  const n = lines.length;

  while (i < n) {
    const line = lines[i];

    // table: header row followed by a |---| separator
    if (line.trimStart().startsWith("|") && i + 1 < n && SEP_RE.test(lines[i + 1])) {
      const head = splitRow(line);
      out.push('<div class="tw"><table><thead><tr>');
      head.forEach((h) => out.push(`<th>${inline(h)}</th>`));
      out.push("</tr></thead><tbody>");
      i += 2;
      while (i < n && lines[i].trimStart().startsWith("|")) {
        out.push("<tr>");
        splitRow(lines[i]).forEach((c) => {
          const cls = cellClass(c);
          out.push(cls ? `<td class="${cls}">${inline(c)}</td>` : `<td>${inline(c)}</td>`);
        });
        out.push("</tr>");
        i += 1;
      }
      out.push("</tbody></table></div>");
      continue;
    }

    if (line.startsWith("### ")) {
      out.push(`<h3>${inline(line.slice(4))}</h3>`);
    } else if (line.startsWith("## ")) {
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
    } else if (line.startsWith("# ")) {
      out.push(`<h1>${inline(line.slice(2))}</h1>`);
    } else if (line.trim() === "---") {
      out.push("<hr>");
    } else if (line.startsWith("> ")) {
      const block: string[] = [];
      while (i < n && lines[i].startsWith(">")) {
        block.push(inline(lines[i].replace(/^>+/, "").trim()));
        i += 1;
      }
      out.push(`<blockquote>${block.join("<br>")}</blockquote>`);
      continue;
    } else if (line.trimStart().startsWith("- ")) {
      out.push("<ul>");
      while (i < n && lines[i].trimStart().startsWith("- ")) {
        const indent = lines[i].length - lines[i].trimStart().length;
        const body = inline(lines[i].trimStart().slice(2));
        out.push(`<li class="${indent ? "sub" : ""}">${body}</li>`);
        i += 1;
      }
      out.push("</ul>");
      continue;
    } else if (line.trim()) {
      out.push(`<p>${inline(line)}</p>`);
    }
    i += 1;
  }
  return out.join("\n");
}
